/**
 * ITAM design system — the shared visual language of the Inventory scene.
 *
 * Every Inventory surface (shell, tabs, asset detail, dialogs) composes these
 * primitives so the feature reads as ONE product: flat bordered cards with a
 * quiet hover lift, tinted-icon stat tiles with tabular numbers, dot+label
 * status pills (never color alone), identity avatars keyed by OS/chassis,
 * consistent table chrome, designed empty states, and a real event timeline.
 *
 * Rules: theme tokens only (no raw hex), 8px spacing rhythm, 160ms ease
 * transitions, one primary CTA per view.
 */
import React from 'react';
import {
  alpha, Avatar, Box, Button, Chip, InputAdornment, LinearProgress, MenuItem,
  Paper, Skeleton, Stack, TableContainer, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import SdStorageOutlinedIcon from '@mui/icons-material/SdStorageOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import UpgradeOutlinedIcon from '@mui/icons-material/UpgradeOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import MergeTypeOutlinedIcon from '@mui/icons-material/MergeTypeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SecurityUpdateWarningOutlinedIcon from '@mui/icons-material/SecurityUpdateWarningOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';

/* ── tokens ─────────────────────────────────────────────────────────────── */

export const EASE = 'all 160ms ease';

/** Panel chrome shared by every ITAM surface — flat, bordered, small radius:
 *  observability-tool panels (Grafana/New Relic), not website cards. */
export const cardSx = (theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1.5,
  backgroundImage: 'none',
  boxShadow: 'none',
});

/* ── full-height table layout ───────────────────────────────────────────────
 * The app scrolls inside its own <main> (App.js: a 100vh flex row, main is
 * `overflow: auto`). A table page fills that box instead of growing past it:
 * the page root takes main's height, every box between it and the table is a
 * flex column with `min-height: 0` (a flex item will not shrink below its
 * content without it), and the table's own container is the ONE scroller. The
 * header, tabs, toolbar and pager keep their natural height and stay put.
 *
 * Only on screens where a fixed-height table is usable: on a phone-width or a
 * very short window there is no room left for rows once the chrome is laid
 * out, so the page scrolls as one long document instead (no nested scroller).
 */
export const FILL_VIEWPORT_MQ = '@media (min-width:900px) and (min-height:560px)';

/** The page root: exactly the height of the scrolling <main>. */
export const fillPageSx = {
  [FILL_VIEWPORT_MQ]: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
};

/** Every box between the page root and the table: takes the space left over. */
export const fillColumnSx = {
  [FILL_VIEWPORT_MQ]: { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 },
};

/** The table's container: the single scroll region (both axes). */
export const fillTableScrollSx = {
  [FILL_VIEWPORT_MQ]: { flex: '1 1 auto', minHeight: 0, maxHeight: 'none', overflow: 'auto' },
};

/** Interactive panel: accent border + faint tint on hover — no lift. */
export const hoverCardSx = (theme, accent = theme.palette.primary.main) => ({
  ...cardSx(theme),
  transition: EASE,
  cursor: 'pointer',
  '&:hover': {
    borderColor: alpha(accent, 0.55),
    backgroundColor: alpha(accent, 0.03),
  },
});

/** Table chrome — enterprise-tool density (Grafana/New Relic): ~34px rows,
 *  slim tinted header band, hairline separators, tabular nums.
 *  NOTE: padding values are EXPLICIT px strings — bare numbers on padding in
 *  sx are spacing units (8px each) and once shipped 40px-tall cell padding. */
export const tableSx = (theme) => ({
  '& th': {
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.palette.divider}`,
    // MUST BE OPAQUE. Every ITAM grid renders with `stickyHeader`, so the header
    // stays put while rows scroll UNDER it — a translucent background (this was
    // alpha(text.primary, 0.03)) let those rows show straight through, and the
    // header read as transparent the moment a table was long enough to scroll.
    // The tint is kept identical by compositing it over an opaque surface with a
    // flat gradient instead of applying it as a see-through colour, so the
    // resting appearance is unchanged and only the scrolled state is fixed.
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(${alpha(theme.palette.text.primary, 0.03)}, `
                   + `${alpha(theme.palette.text.primary, 0.03)})`,
    padding: '6px 10px',
    height: 32,
    boxSizing: 'border-box',
  },
  '& td': {
    fontSize: 12.5,
    lineHeight: 1.4,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    fontVariantNumeric: 'tabular-nums',
    padding: '5px 10px',
    height: 34,
    boxSizing: 'border-box',
  },
  '& td:first-of-type, & th:first-of-type': { paddingLeft: '14px' },
  '& td:last-of-type, & th:last-of-type': { paddingRight: '14px' },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
  '& tbody tr': { transition: EASE },
  '& tbody tr:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
  '& .MuiTableSortLabel-root': { fontSize: 10, letterSpacing: '0.07em' },
  '& .MuiIconButton-root': { padding: '3px' },
  '& .MuiCheckbox-root': { padding: '4px' },
});

/* ── status metadata (single source of truth for pills) ─────────────────── */

export const STATUS_META = {
  // lifecycle
  active:   { label: 'Active',   tone: 'success' },
  stale:    { label: 'Stale',    tone: 'warning' },
  retired:  { label: 'Retired',  tone: 'default' },
  // agent
  online:   { label: 'Online',   tone: 'success', pulse: true },
  offline:  { label: 'Offline',  tone: 'error' },
  // presence (device/network-inferred)
  office:   { label: 'Office',   tone: 'success' },
  remote:   { label: 'Remote',   tone: 'info' },
  unknown:  { label: 'Unknown',  tone: 'default' },
};

const toneColor = (theme, tone) => ({
  success: theme.palette.success.main,
  warning: theme.palette.warning.main,
  error: theme.palette.error.main,
  info: theme.palette.info.main,
  default: theme.palette.text.disabled,
}[tone] || theme.palette.text.disabled);

/** Dot + label status pill. `status` keys STATUS_META; overrides via props. */
export function StatusPill({ status, label, tone, size = 'small', sx }) {
  const theme = useTheme();
  const meta = STATUS_META[String(status || '').toLowerCase()] || {};
  const c = toneColor(theme, tone || meta.tone || 'default');
  const text = label || meta.label || (status ? String(status) : '—');
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.6,
        px: 0.9, py: 0, height: size === 'small' ? 20 : 24,
        borderRadius: '4px', fontSize: size === 'small' ? 11 : 12.5, fontWeight: 600,
        color: c, backgroundColor: alpha(c, 0.12), lineHeight: 1.7,
        whiteSpace: 'nowrap', ...sx,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 7, height: 7, borderRadius: '50%', backgroundColor: c, flexShrink: 0,
          ...(meta.pulse ? {
            boxShadow: `0 0 0 0 ${alpha(c, 0.5)}`,
            animation: 'itamPulse 2s infinite',
            '@keyframes itamPulse': {
              '0%': { boxShadow: `0 0 0 0 ${alpha(c, 0.45)}` },
              '70%': { boxShadow: `0 0 0 5px ${alpha(c, 0)}` },
              '100%': { boxShadow: `0 0 0 0 ${alpha(c, 0)}` },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          } : {}),
        }}
      />
      {text}
    </Box>
  );
}

/* ── identity ───────────────────────────────────────────────────────────── */

/** Device icon by OS / chassis / kind. */
export function deviceIcon({ osType, chassisType, agentKind } = {}, props = {}) {
  const os = String(osType || '').toLowerCase();
  const chassis = String(chassisType || '').toLowerCase();
  if (chassis === 'vm') return <CloudOutlinedIcon {...props} />;
  if (chassis === 'server' || agentKind === 'server') return <DnsOutlinedIcon {...props} />;
  if (chassis === 'laptop') return <LaptopMacOutlinedIcon {...props} />;
  if (os === 'windows') return <DesktopWindowsOutlinedIcon {...props} />;
  if (os === 'linux') return <TerminalOutlinedIcon {...props} />;
  if (os === 'macos' || os === 'darwin') return <LaptopMacOutlinedIcon {...props} />;
  return <DevicesOtherOutlinedIcon {...props} />;
}

/** Tinted square identity avatar for a device row / detail hero. */
export function DeviceAvatar({ host = {}, size = 36, accent, sx }) {
  const theme = useTheme();
  const c = accent || theme.palette.primary.main;
  return (
    <Avatar
      variant="rounded"
      sx={{
        width: size, height: size, borderRadius: size >= 48 ? 2.5 : 2,
        bgcolor: alpha(c, 0.12), color: c, ...sx,
      }}
    >
      {deviceIcon(host, { sx: { fontSize: size * 0.55 } })}
    </Avatar>
  );
}

/** Initials avatar for a user association. */
export function UserAvatar({ name = '', size = 28, sx }) {
  const theme = useTheme();
  const clean = String(name).split('\\').pop() || '';
  const initials = clean.split(/[\s._-]+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join('') || '?';
  const palette = [
    theme.palette.primary.main, theme.palette.info.main, theme.palette.success.main,
    theme.palette.warning.main, theme.palette.secondary.main,
  ];
  let h = 0;
  for (let i = 0; i < clean.length; i += 1) h = (h * 31 + clean.charCodeAt(i)) >>> 0; // eslint-disable-line no-bitwise
  const c = palette[h % palette.length];
  return (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.42, fontWeight: 700, bgcolor: alpha(c, 0.16), color: c, ...sx }}>
      {initials}
    </Avatar>
  );
}

/* ── layout primitives ──────────────────────────────────────────────────── */

/** KPI stat panel — Grafana-style: caps label row with a small accent icon,
 *  big tabular value. Flat, dense, no decoration. */
export function StatTile({ icon, label, value, hint, accent, onClick, loading, extra, ariaLabel, selected }) {
  const theme = useTheme();
  const c = accent || theme.palette.primary.main;
  // A clickable tile is a button to the keyboard and to assistive tech too —
  // a bare clickable <div> is reachable by mouse only.
  const interactive = onClick ? {
    role: 'button',
    tabIndex: 0,
    'aria-label': ariaLabel,
    'aria-pressed': selected === undefined ? undefined : !!selected,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
    },
  } : {};
  return (
    <Paper
      onClick={onClick}
      {...interactive}
      sx={{
        ...(onClick ? {
          '&:focus-visible': { outline: `2px solid ${c}`, outlineOffset: 2 },
        } : {}),
        ...(onClick ? hoverCardSx(theme, c) : cardSx(theme)),
        px: 1.75, py: 1.25, minWidth: 0,
        ...(selected ? { borderColor: alpha(c, 0.6), backgroundColor: alpha(c, 0.04) } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: 10 }} noWrap>
          {label}
        </Typography>
        <Box sx={{ color: c, display: 'flex', '& svg': { fontSize: 15 } }}>{icon}</Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        {loading ? <Skeleton width={56} height={28} /> : (
          <Typography sx={{ fontSize: 21, fontWeight: 700, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }} noWrap>
            {value}
          </Typography>
        )}
        {hint ? (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10.5 }} noWrap>{hint}</Typography>
        ) : null}
        {extra}
      </Box>
    </Paper>
  );
}

/** Section card with a consistent header row. */
export function SectionCard({
  title, subtitle, action, icon, children, sx, contentSx, id, titleComponent,
}) {
  const theme = useTheme();
  // Optional semantics: with an `id` the card becomes a labelled <section>
  // whose title is a real heading (`titleComponent`, e.g. 'h3'), so screen
  // readers can jump between cards. Without them it renders as before.
  const titleId = id ? `${id}-title` : undefined;
  return (
    <Paper
      sx={{ ...cardSx(theme), overflow: 'hidden', ...sx }}
      {...(id ? { component: 'section', id, 'aria-labelledby': titleId } : {})}
    >
      {(title || action) && (
        <Box sx={{
          px: 1.75, py: 1, display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          minHeight: 40,
        }}>
          {icon ? (
            <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 17 } }}>{icon}</Box>
          ) : null}
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              id={titleId}
              component={titleComponent || 'p'}
              sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.3, m: 0 }}
              noWrap
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }} noWrap>{subtitle}</Typography>
            ) : null}
          </Box>
          {action}
        </Box>
      )}
      <Box sx={{ p: 1.75, ...contentSx }}>{children}</Box>
    </Paper>
  );
}

/** Label/value pair for identity + fact grids. */
export function KeyFact({ label, value, mono, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{
        color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em',
        textTransform: 'uppercase', fontSize: 10, display: 'block', mb: 0.25,
      }}>
        {label}
      </Typography>
      {children || (
        <Typography sx={{
          fontSize: 13.5, fontWeight: 500, wordBreak: 'break-word',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
        }}>
          {value === null || value === undefined || value === '' ? '—' : value}
        </Typography>
      )}
    </Box>
  );
}

/** Search + filter toolbar used above every ITAM table. */
export function TableToolbar({ search, onSearch, placeholder = 'Search…', children, right, count }) {
  return (
    <Box sx={{ px: 1.5, py: 0.75, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {onSearch ? (
        <TextField
          size="small"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={placeholder}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
            '& .MuiOutlinedInput-input': { py: 0.45, fontSize: 12.5 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>
            ),
          }}
        />
      ) : null}
      {children}
      <Box sx={{ flex: 1 }} />
      {typeof count === 'string' || typeof count === 'number' ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{count}</Typography>
      ) : null}
      {right}
    </Box>
  );
}

/** Filter chip strip (single-select segmented filter). */
export function FilterChips({ options, value, onChange }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
      {options.map((o) => {
        const selected = o.value === value;
        const c = o.color || theme.palette.primary.main;
        return (
          <Chip
            key={String(o.value)}
            size="small"
            label={o.count !== undefined ? `${o.label} · ${o.count}` : o.label}
            onClick={() => onChange(o.value)}
            sx={{
              fontWeight: 600, borderRadius: 5, transition: EASE,
              backgroundColor: selected ? alpha(c, 0.15) : 'transparent',
              color: selected ? c : 'text.secondary',
              border: `1px solid ${selected ? alpha(c, 0.4) : theme.palette.divider}`,
              '&:hover': { backgroundColor: alpha(c, 0.1) },
            }}
          />
        );
      })}
    </Stack>
  );
}

/** Designed empty state with optional CTA. */
export function EmptyState({ icon, title, body, actionLabel, onAction, compact, sx }) {
  const theme = useTheme();
  return (
    <Box sx={{ textAlign: 'center', py: compact ? 3 : 5, px: 3, ...sx }}>
      <Avatar sx={{
        width: compact ? 40 : 52, height: compact ? 40 : 52, mx: 'auto', mb: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main,
        borderRadius: 1.5,
        '& svg': { fontSize: compact ? 20 : 26 },
      }} variant="rounded">
        {icon || <DevicesOtherOutlinedIcon />}
      </Avatar>
      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>{title}</Typography>
      {body ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto', mb: actionLabel ? 2 : 0 }}>
          {body}
        </Typography>
      ) : null}
      {actionLabel ? (
        <Button variant="contained" size="small" onClick={onAction} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}

/** Proportional segmented bar (fleet health, usage splits). segments:
 *  [{label, value, color}] — zero-total renders a muted track. */
export function SegmentBar({ segments, height = 8, sx }) {
  const theme = useTheme();
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ display: 'flex', height, borderRadius: height, overflow: 'hidden', backgroundColor: alpha(theme.palette.text.primary, 0.06) }}>
        {total > 0 && segments.filter((s) => s.value > 0).map((s) => (
          <Tooltip key={s.label} title={`${s.label}: ${s.value}`}>
            <Box sx={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color, transition: EASE }} />
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        {segments.map((s) => (
          <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.color }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {s.label} <b>{s.value}</b>
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/* ── asset timeline ─────────────────────────────────────────────────────── */

/**
 * THE asset-event registry. Kept in exact parity with
 * `AssetEventService.EVENT_TYPES` on the backend by
 * `AssetEventRegistryParityTest`, which parses the keys out of THIS object
 * literal and fails the build on any drift in either direction.
 *
 * Adding a key here without a backend emitter (or vice versa) breaks the build
 * — that is the point. `agent_updated` sat here through Wave 1 with nothing
 * emitting it and was removed in Wave 2 / G1; it is back in Wave 2 / G2
 * together with its emitter (`AgentUpdateService.reportStatus`, terminal
 * outcomes only). `eventMeta()` below still renders an unknown type rather than
 * throwing, so a stale client against a newer backend degrades gracefully.
 */
export const EVENT_META = {
  host_discovered:   { icon: <AddCircleOutlineOutlinedIcon />, tone: 'success', group: 'device',   label: 'Discovered' },
  identity_adopted:  { icon: <FingerprintOutlinedIcon />,      tone: 'info',    group: 'device',   label: 'Identity' },
  hostname_changed:  { icon: <BadgeOutlinedIcon />,            tone: 'info',    group: 'device',   label: 'Hostname' },
  os_changed:        { icon: <UpgradeOutlinedIcon />,          tone: 'info',    group: 'device',   label: 'OS' },
  hardware_changed:  { icon: <MemoryOutlinedIcon />,           tone: 'warning', group: 'hardware', label: 'Hardware' },
  user_changed:      { icon: <PersonOutlineOutlinedIcon />,    tone: 'info',    group: 'users',    label: 'User' },
  software_installed:{ icon: <AddCircleOutlineOutlinedIcon />, tone: 'success', group: 'software', label: 'Installed' },
  software_removed:  { icon: <RemoveCircleOutlineOutlinedIcon />, tone: 'error', group: 'software', label: 'Removed' },
  software_updated:  { icon: <UpgradeOutlinedIcon />,          tone: 'info',    group: 'software', label: 'Updated' },
  network_changed:   { icon: <WifiOutlinedIcon />,             tone: 'info',    group: 'network',  label: 'Network' },
  presence_changed:  { icon: <ApartmentOutlinedIcon />,        tone: 'info',    group: 'presence', label: 'Presence' },
  agent_enrolled:    { icon: <RocketLaunchOutlinedIcon />,     tone: 'success', group: 'agent',    label: 'Enrolled' },
  agent_key_revoked: { icon: <VpnKeyOutlinedIcon />,           tone: 'error',   group: 'agent',    label: 'Key revoked' },
  agent_updated:     { icon: <SystemUpdateAltOutlinedIcon />,  tone: 'info',    group: 'agent',    label: 'Agent update' },
  // Scout group provenance — the agent's fleet group MOVED (a first join, or a
  // declaration withdrawing the platform-assigned groups it no longer names).
  // Toned 'info' rather than 'warning': a move that follows the machine's own
  // configuration is the system working, and the timeline entry is there to say
  // WHICH group won and why, which is exactly what was previously unanswerable.
  agent_group_changed: { icon: <GroupWorkOutlinedIcon />,      tone: 'info',    group: 'agent',    label: 'Fleet group' },
  lifecycle_changed: { icon: <HistoryOutlinedIcon />,          tone: 'warning', group: 'device',   label: 'Lifecycle' },
  license_position_changed: { icon: <WorkspacePremiumOutlinedIcon />, tone: 'warning', group: 'software', label: 'License' },
  asset_imported:      { icon: <UploadFileOutlinedIcon />,     tone: 'info',    group: 'device',    label: 'Imported' },
  asset_import_merged: { icon: <MergeTypeOutlinedIcon />,      tone: 'success', group: 'device',    label: 'Merged' },
  financials_updated:  { icon: <PaymentsOutlinedIcon />,       tone: 'info',    group: 'financial', label: 'Financials' },
  warranty_expiring:   { icon: <ShieldOutlinedIcon />,         tone: 'warning', group: 'financial', label: 'Warranty' },
  patch_posture_changed: { icon: <SecurityUpdateWarningOutlinedIcon />, tone: 'warning', group: 'device', label: 'Update posture' },
  // An ADMIN authored a product by hand. Every other product in the table was
  // minted by normalization from a rule match or an executable fallback, so
  // provenance is the whole point of this entry: when someone later asks why a
  // product exists with no matching rule behind it, this is the answer.
  product_created:     { icon: <AddCircleOutlineOutlinedIcon />, tone: 'success', group: 'software', label: 'Product created' },
  product_merged:      { icon: <MergeTypeOutlinedIcon />,      tone: 'success', group: 'software',  label: 'Product merged' },
  vulnerability_detected: { icon: <BugReportOutlinedIcon />,   tone: 'error',   group: 'software',  label: 'Vulnerability' },
  // ITAM Wave 2 follow-ups — a tenant ADMIN acknowledged, dismissed or reopened
  // a needs-review finding. Toned 'info', not 'error': it is a judgement being
  // recorded, not a new problem, and the timeline is where that judgement has to
  // be visible when someone later asks why an item stopped appearing.
  vulnerability_reviewed: { icon: <FactCheckOutlinedIcon />,    tone: 'info',    group: 'software',  label: 'Vulnerability review' },
  // ITAM Wave 2 / G4 — the entity-fold primitive and the orphan-product
  // discard that is its product-side counterpart. Both are irreversible
  // operator actions and are toned as such: the fold reads as a device
  // identity change (it is one), the discard as a removal.
  entity_folded:       { icon: <MergeTypeOutlinedIcon />,      tone: 'warning', group: 'device',    label: 'Hosts merged' },
  product_discarded:   { icon: <RemoveCircleOutlineOutlinedIcon />, tone: 'error', group: 'software', label: 'Product discarded' },
  // An admin decided what an UNRECOGNISED INSTALL is — attached it to a
  // product, authored a matching rule, marked it not-a-product, or sent it back
  // to the queue. One entry for the whole workflow, like vulnerability_reviewed
  // above: the timeline question is "who decided what, and when", and the
  // decision itself is in the detail. Toned 'info' — recording a judgement is
  // not a new problem, and this is the row that answers "why did that title
  // stop appearing in the queue".
  install_reviewed:    { icon: <HelpOutlineOutlinedIcon />,    tone: 'info',    group: 'software',  label: 'Install reviewed' },
};

export function eventMeta(type) {
  return EVENT_META[type] || { icon: <HistoryOutlinedIcon />, tone: 'default', group: 'device', label: type || 'Event' };
}

/**
 * Vertical timeline for blend_asset_events rows
 * [{eventType, summary, createdAt, entityName?, entityId?}].
 *
 * When a row carries `entityName` it is rendered as the device the event belongs
 * to. On an org-wide feed that name is the difference between a readable list and
 * a column of "Software installed" with no indication of where — the backend has
 * always returned it; this component used to drop it.
 *
 * `onSelect` makes the device name a jump target; without it the name still
 * renders, just not clickable.
 */
export function Timeline({ events, renderTime, onSelect }) {
  const theme = useTheme();
  return (
    <Box sx={{ position: 'relative', pl: 0.5 }}>
      {events.map((ev, i) => {
        const meta = eventMeta(ev.eventType);
        const c = toneColor(theme, meta.tone);
        const last = i === events.length - 1;
        return (
          <Box key={ev.id || i} sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: last ? 0 : 1.75 }}>
            {!last && (
              <Box sx={{ position: 'absolute', left: 13, top: 28, bottom: 0, width: 2, backgroundColor: alpha(theme.palette.divider, 0.8) }} />
            )}
            <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(c, 0.12), color: c, '& svg': { fontSize: 14 }, zIndex: 1 }}>
              {meta.icon}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1, pt: 0.1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}>{ev.summary || meta.label}</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                  {renderTime ? renderTime(ev.createdAt) : ev.createdAt}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: c, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
                  {meta.label}
                </Typography>
                {/* The device this happened to. An entity deleted after the event
                    leaves entityName null, so the row degrades instead of lying. */}
                {ev.entityName && (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>·</Typography>
                    <Typography
                      variant="caption"
                      onClick={onSelect && ev.entityId ? () => onSelect(ev) : undefined}
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: 'text.secondary',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        ...(onSelect && ev.entityId ? {
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                        } : null),
                      }}
                    >
                      {ev.entityName}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/* ── misc ───────────────────────────────────────────────────────────────── */

/** Presence pill wrapper honoring the "network-inferred" framing. */
export function PresencePill({ presence, location }) {
  const pill = <StatusPill status={presence || 'unknown'} label={presence === 'office' && location ? `Office · ${location}` : undefined} />;
  return presence ? (
    <Tooltip title="Network-based presence — inferred from trusted office networks, not guaranteed physical presence.">
      <span>{pill}</span>
    </Tooltip>
  ) : <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
}

export const ITAM_ICONS = {
  devices: <DevicesOtherOutlinedIcon />,
  apps: <AppsOutlinedIcon />,
  users: <PersonOutlineOutlinedIcon />,
  network: <WifiOutlinedIcon />,
  security: <SecurityOutlinedIcon />,
  storage: <SdStorageOutlinedIcon />,
  memory: <MemoryOutlinedIcon />,
  office: <ApartmentOutlinedIcon />,
  remote: <PublicOutlinedIcon />,
  unknown: <HelpOutlineOutlinedIcon />,
  deploy: <RocketLaunchOutlinedIcon />,
  vulnerability: <BugReportOutlinedIcon />,
};

/* ── pagination ─────────────────────────────────────────────────────────── */

/** Rows-per-page choices shared by every server-paginated ITAM table. */
export const ROWS_PER_PAGE = [25, 50, 100, 250];

/** "51–100 of 3,412" — the range THIS page covers within the whole filtered set. */
export function pageRangeLabel(page, pageSize, total) {
  const t = Number(total) || 0;
  if (t === 0) return '0 of 0';
  const from = page * pageSize + 1;
  const to = Math.min(t, (page + 1) * pageSize);
  return `${from.toLocaleString()}–${to.toLocaleString()} of ${t.toLocaleString()}`;
}

/**
 * THE pagination control for ITAM tables — one affordance, composed from the
 * same tokens as `tableSx` so it reads as part of the table rather than a
 * bolted-on MUI widget: 12.5px type, 24px controls, tabular numbers, a hairline
 * top rule, no elevation.
 *
 * <p>Deliberately NOT `TablePagination`: that component insists on its own
 * 52px-tall toolbar and a `TableCell`/`colSpan` host, which fights sticky
 * headers and doubles the density of a 34px-row grid.
 *
 * <p>Renders whenever a total is known — including on a single page, where it
 * still answers "how many are there?", the question the old client-side tables
 * could only answer because they had already fetched everything.
 */
export function TablePager({
  page = 0, pageSize = 50, total = 0, onPage, onPageSize,
  rowsPerPageOptions = ROWS_PER_PAGE, loading = false, disabled = false,
}) {
  const theme = useTheme();
  const pages = pageSize > 0 ? Math.ceil((Number(total) || 0) / pageSize) : 0;
  const last = Math.max(0, pages - 1);
  const busy = disabled || loading;
  const navBtn = {
    minWidth: 26, width: 26, height: 24, p: 0, borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`, color: 'text.secondary',
    '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.5) },
    '&.Mui-disabled': { borderColor: alpha(theme.palette.divider, 0.5) },
  };
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
        px: 1.75, py: 0.75, minHeight: 38,
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.text.primary, 0.015),
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {pageRangeLabel(page, pageSize, total)}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>Rows</Typography>
      <TextField
        select size="small" value={pageSize} disabled={busy}
        onChange={(e) => onPageSize?.(Number(e.target.value))}
        SelectProps={{ MenuProps: { disablePortal: false } }}
        sx={{
          '& .MuiOutlinedInput-root': { borderRadius: 1 },
          '& .MuiSelect-select': {
            py: 0.15, pl: 0.75, fontSize: 12, minHeight: 0,
            fontVariantNumeric: 'tabular-nums',
          },
        }}
      >
        {rowsPerPageOptions.map((n) => (
          <MenuItem key={n} value={n} sx={{ fontSize: 12.5 }}>{n}</MenuItem>
        ))}
      </TextField>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', mx: 0.5 }}
      >
        {`Page ${(pages === 0 ? 0 : page + 1).toLocaleString()} of ${pages.toLocaleString()}`}
      </Typography>
      <Stack direction="row" spacing={0.5}>
        <Button size="small" sx={navBtn} disabled={busy || page <= 0} onClick={() => onPage?.(0)} aria-label="First page">«</Button>
        <Button size="small" sx={navBtn} disabled={busy || page <= 0} onClick={() => onPage?.(page - 1)} aria-label="Previous page">‹</Button>
        <Button size="small" sx={navBtn} disabled={busy || page >= last} onClick={() => onPage?.(page + 1)} aria-label="Next page">›</Button>
        <Button size="small" sx={navBtn} disabled={busy || page >= last} onClick={() => onPage?.(last)} aria-label="Last page">»</Button>
      </Stack>
    </Box>
  );
}

/**
 * Table body wrapper that keeps its HEIGHT while a page is in flight.
 *
 * <p>A server round-trip per page/sort/search means the naive "render a spinner
 * instead of the table" collapses the page and the whole layout jumps — twice
 * per keystroke with a debounced search. Instead the previous page stays mounted
 * at reduced opacity with pointer events off, and a `minHeight` derived from the
 * page size holds the shape open on the FIRST load, when there is nothing to
 * keep.
 */
export function TableViewport({
  loading, rowCount, pageSize = 50, rowHeight = 34, maxHeight, fill = false, children, sx,
}) {
  const reserved = Math.max(1, Math.min(rowCount || pageSize, pageSize)) * rowHeight;
  // The reserved height must never exceed the cap. CSS lets min-height beat
  // max-height, so a 50-row page reserved 1700px inside a "calc(100vh - 340px)"
  // cap: the table scrolled inside a box taller than the screen, and the page
  // scrolled too, pushing the pager off the bottom.
  const cap = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
  return (
    <Box sx={{ position: 'relative', ...(fill ? fillColumnSx : null), ...sx }}>
      {loading ? (
        <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 3 }} />
      ) : null}
      <TableContainer
        data-scroll-region={fill ? 'table' : undefined}
        sx={{
          maxHeight: fill ? undefined : maxHeight,
          minHeight: cap && !fill ? `min(${reserved}px, ${cap})` : reserved,
          transition: EASE,
          opacity: loading ? 0.55 : 1,
          pointerEvents: loading ? 'none' : 'auto',
          ...(fill ? fillTableScrollSx : null),
        }}
      >
        {children}
      </TableContainer>
    </Box>
  );
}
